import { AnimatePresence, motion } from "framer-motion";
import { Globe, X } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { springs } from "@/components/ui/motion";
import { useChatContext } from "@/stores/chat-context-store";
import { ENTITY_ICONS } from "./entity-icons";

export function ChatContextChips() {
  const { t } = useTranslation(["chat"]);
  const ambient = useChatContext((s) => s.ambient);
  const entities = useChatContext((s) => s.entities);
  const removeEntity = useChatContext((s) => s.removeEntity);

  const hasContent = ambient !== null || entities.length > 0;
  if (!hasContent) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-3.5 pt-3 pb-1">
      <AnimatePresence mode="popLayout">
        {/* Ambient chip (page context, no remove button) */}
        {ambient ? (
          <motion.span
            key="ambient"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/80 border border-border/30 px-2.5 py-1 text-[10.5px] font-semibold text-accent uppercase tracking-wider leading-tight shadow-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={springs.smooth}
            aria-label={t("context.currentPageLabel")}
          >
            <Globe size={10} className="shrink-0 opacity-70" />
            {ambient.label}
          </motion.span>
        ) : null}

        {/* Entity chips (with remove button) */}
        {entities.map((entity) => {
          const Icon = ENTITY_ICONS[entity.type];
          return (
            <motion.span
              key={entity.id}
              className="group inline-flex items-center gap-1.5 rounded-lg bg-white/80 border border-border/30 px-2.5 py-1 text-[10.5px] font-medium text-secondary leading-tight shadow-sm hover:border-accent/30 transition-colors duration-150"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={springs.smooth}
              layout
            >
              <Icon size={10} className="shrink-0 text-accent/60" />
              <span className="max-w-[120px] truncate">{entity.label}</span>
              <button
                type="button"
                onClick={() => removeEntity(entity.id)}
                className="opacity-0 group-hover:opacity-100 hover:text-primary transition-all duration-150 ml-0.5 cursor-pointer"
                aria-label={t("context.removeEntity", {
                  label: entity.label,
                })}
              >
                <X size={9} strokeWidth={3} />
              </button>
            </motion.span>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
